import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Phone, Siren, Flame, ArrowRight } from "lucide-react";
import { BUSINESS, NAV } from "../../content/site";
import { MobileMenu } from "./MobileMenu";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const AnnouncementBar = () => (
  <div className="bg-plum-deep text-white" data-testid="announcement-bar">
    <div className="container-x flex h-9 items-center justify-center gap-2 text-xs sm:text-sm">
      <Siren className="h-4 w-4 text-gold" aria-hidden="true" />
      <span className="font-medium">Plumbing emergency in Asheville?</span>
      <a href={BUSINESS.phoneHref} className="inline-block py-2 font-bold text-gold underline-offset-4 hover:underline" data-testid="announcement-call-link">
        Call {BUSINESS.phone}
      </a>
      <Link to="/emergency-plumbing" className="hidden font-semibold text-white/80 hover:text-white sm:inline" data-testid="announcement-emergency-link">
        Emergency service
      </Link>
    </div>
  </div>
);

const MegaPanel = ({ group, id, onNavigate }) => (
  <div id={id} role="region" aria-label={`${group.label} menu`} className="absolute inset-x-0 top-full z-40 border-t border-gray-100 bg-white shadow-2xl" data-testid={`mega-panel-${slug(group.label)}`}>
    <div className="container-x grid gap-10 py-8 lg:grid-cols-12">
      <div className={`grid gap-8 ${group.promo ? "lg:col-span-8" : "lg:col-span-12"} sm:grid-cols-2`}>
        {group.columns.map((col) => (
          <div key={col.heading}>
            <p className="eyebrow mb-4 text-plum/70">{col.heading}</p>
            <ul className="space-y-1">
              {col.links.map(([label, href]) => (
                <li key={href + label}>
                  <Link to={href} onClick={onNavigate} className="group flex items-center justify-between rounded-lg px-3 py-2 text-[15px] font-medium text-ink transition-colors hover:bg-plum-soft hover:text-plum" data-testid={`mega-link-${slug(label)}`}>
                    {label}
                    <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 transition-[opacity,transform] group-hover:translate-x-0 group-hover:opacity-100" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {group.promo && (
        <div className="relative overflow-hidden rounded-2xl bg-plum p-7 text-white lg:col-span-4" data-testid={`mega-promo-${slug(group.label)}`}>
          <Flame className="mb-4 h-8 w-8 text-gold" aria-hidden="true" />
          <p className="font-display text-2xl font-bold">{group.promo.title}</p>
          <p className="mt-2 text-sm text-white/80">{group.promo.text}</p>
          {group.promo.href.startsWith("tel:") ? (
            <a href={group.promo.href} className="btn-gold mt-6">{group.promo.cta}</a>
          ) : (
            <Link to={group.promo.href} onClick={onNavigate} className="btn-gold mt-6">{group.promo.cta}</Link>
          )}
          <div className="pointer-events-none absolute -bottom-10 -right-10 h-40 w-40 rounded-full bg-gold/20" aria-hidden="true" />
        </div>
      )}
    </div>
  </div>
);

const DesktopNav = () => {
  const [open, setOpen] = useState(null);
  const ref = useRef(null);
  const hoverTimer = useRef(null);
  const location = useLocation();
  const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  useEffect(() => setOpen(null), [location.pathname, location.hash]);
  useEffect(() => {
    const onDoc = (e) => ref.current && !ref.current.contains(e.target) && setOpen(null);
    const onKey = (e) => {
      if (e.key === "Escape" && open !== null) {
        ref.current?.querySelector(`[data-index="${open}"]`)?.focus();
        setOpen(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const hover = (i) => {
    if (!canHover) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(i), i === null ? 180 : 90);
  };

  return (
    <nav aria-label="Primary" ref={ref} className="relative hidden border-t border-gray-100 lg:block" onMouseLeave={() => hover(null)}>
      <ul className="container-x flex items-center justify-between">
        {NAV.map((g, i) => (
          <li key={g.label} onMouseEnter={() => hover(i)}>
            <button
              type="button"
              data-index={i}
              aria-expanded={open === i}
              aria-controls={`mega-${slug(g.label)}`}
              onClick={() => setOpen(open === i ? null : i)}
              className={`flex items-center gap-1 border-b-[3px] px-2 py-3.5 text-[15px] font-semibold transition-colors xl:px-3 ${open === i ? "border-gold text-plum" : "border-transparent text-ink hover:text-plum"}`}
              data-testid={`mega-trigger-${slug(g.label)}`}
            >
              {g.label}
              <ChevronDown className={`h-4 w-4 transition-transform ${open === i ? "rotate-180" : ""}`} aria-hidden="true" />
            </button>
          </li>
        ))}
        <li>
          <Link to="/blog" className="px-2 py-3.5 text-[15px] font-semibold text-ink hover:text-plum" data-testid="nav-blog-link">Blog</Link>
        </li>
        <li>
          <Link to="/contact" className="px-2 py-3.5 text-[15px] font-semibold text-ink hover:text-plum" data-testid="nav-contact-link">Contact</Link>
        </li>
      </ul>
      {open !== null && <MegaPanel group={NAV[open]} id={`mega-${slug(NAV[open].label)}`} onNavigate={() => setOpen(null)} />}
    </nav>
  );
};

export const Header = () => (
  <header className="sticky top-0 z-50 bg-white/95 shadow-[0_1px_0_rgba(0,0,0,0.06)] backdrop-blur" data-testid="site-header">
    <div className="container-x flex h-16 items-center justify-between gap-4 lg:h-[84px]">
      <Link to="/" className="shrink-0" aria-label="PlumbSmart Plumbing & Drains home" data-testid="header-logo-link">
        <img src="https://irp.cdn-website.com/90d98ddf/dms3rep/multi/opt/plumb-smart-logo-200w.png" srcSet="https://irp.cdn-website.com/90d98ddf/dms3rep/multi/opt/plumb-smart-logo-200w.png 200w, https://irp.cdn-website.com/90d98ddf/dms3rep/multi/opt/plumb-smart-logo-300w.png 300w" sizes="(min-width:1024px) 143px, 98px" alt="PlumbSmart Plumbing & Drains logo" width="400" height="179" className="h-11 w-auto lg:h-16" />
      </Link>
      <div className="hidden items-center gap-5 lg:flex">
        <a href={BUSINESS.phoneHref} className="flex items-center gap-3 text-plum" data-testid="header-phone-link">
          <span className="grid h-10 w-10 place-items-center rounded-full bg-gold-soft"><Phone className="h-5 w-5" aria-hidden="true" /></span>
          <span className="leading-tight">
            <span className="block text-xs font-medium text-ink-soft">Call PlumbSmart</span>
            <span className="font-display text-xl font-bold">{BUSINESS.phone}</span>
          </span>
        </a>
        <Link to="/free-water-heater-replacement" className="btn-outline py-2.5" data-testid="header-water-heater-estimate-btn">Free Water Heater Estimate</Link>
        <Link to="/schedule-service" className="btn-gold py-2.5" data-testid="header-schedule-service-btn">Schedule Service</Link>
      </div>
      <div className="flex items-center gap-2 lg:hidden">
        <a href={BUSINESS.phoneHref} aria-label={`Call ${BUSINESS.phone}`} className="hidden h-11 w-11 place-items-center rounded-full bg-plum text-white min-[400px]:grid" data-testid="mobile-call-btn">
          <Phone className="h-5 w-5" aria-hidden="true" />
        </a>
        {/* The sticky bottom bar already offers Call and Schedule Service on phones, so the header carries the second main CTA; the call icon shows only where it fits. */}
        <Link to="/free-water-heater-replacement" className="btn-gold min-h-[44px] whitespace-nowrap px-3.5 py-2 text-sm" data-testid="mobile-estimate-btn">Free Estimate<span className="sr-only"> on a water heater</span></Link>
        <MobileMenu />
      </div>
    </div>
    <DesktopNav />
  </header>
);
